import type { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { Button, Card } from "@sellar-io/components";
import { formatCurrency } from "@sellar-io/common";
import dayjs from "dayjs";
import { useState } from "react";
import { Alert } from "../../../../components/alert/Alert";
import { useOrderExternalRefundConfirmation } from "../../../../hooks/api/v2/order/useOrderExternalRefundConfirmation";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { OrderExternalRefundDialog } from "../../modals/OrderExternalRefundDialog";

export function OrderExternalRefundPanel({
  order,
}: {
  order: OrderGetResponseType;
}) {
  const { business } = useSellarCurrentBusiness();
  const flags = useRemoteFeatureFlags();
  if (
    !business?.BusinessUser ||
    business.Supplier?.id !== order.SupplierId ||
    !order.voidedAt ||
    order.fulfillmentType !== "sellarDistribution" ||
    !flags.isActive("sdWholeOrderExceptionHandling")
  ) {
    return null;
  }
  return (
    <ExternalRefundPanelContent
      key={`${business.id}:${order.id}`}
      order={order}
    />
  );
}

function ExternalRefundPanelContent({
  order,
}: {
  order: OrderGetResponseType;
}) {
  const query = useOrderExternalRefundConfirmation(order.id);
  const [dialogActive, setDialogActive] = useState(false);
  const data = query.data;

  if (data && !data.enabled) {
    return null;
  }
  if (!data && !query.isError) {
    return <p role="status">Loading external refund details…</p>;
  }
  if (query.isError && !data) {
    return (
      <Alert
        type="error"
        message="Unable to load external refund details. Please try again."
      >
        <Button loading={query.isFetching} onClick={() => void query.refetch()}>
          Try again
        </Button>
      </Alert>
    );
  }
  if (!data) {
    return null;
  }
  if (!data.canRecord && !data.confirmation && data.remainingAmount > 0) {
    return null;
  }

  const confirmation = data.confirmation;
  return (
    <Card className="flex flex-col gap-3 bg-white p-4">
      <h2 className="text-lg font-semibold">External refund</h2>
      {query.isError && (
        <Alert
          type="error"
          message="Unable to refresh external refund details. Please try again."
        >
          <Button
            loading={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Try again
          </Button>
        </Alert>
      )}
      {confirmation ? (
        <>
          <p role="status">
            Your business confirmed an external refund of{" "}
            {formatCurrency(confirmation.amount, order.currency)} on{" "}
            {dayjs(confirmation.recordedAt).format("DD/MM/YYYY [at] HH:mm")}.
          </p>
          {confirmation.recordedByEmail && (
            <p className="break-words">
              Recorded by {confirmation.recordedByEmail}
            </p>
          )}
          {confirmation.reference && (
            <p className="break-words">Reference: {confirmation.reference}</p>
          )}
          {confirmation.note && (
            <p className="break-words whitespace-pre-wrap">
              Note: {confirmation.note}
            </p>
          )}
          <p className="text-sm text-gray-600">
            This is your supplier confirmation, not a separate provider refund.
            Provider records may update later.
          </p>
        </>
      ) : (
        <>
          <p>
            Recording a refund is optional. You and the buyer may agree another
            arrangement, such as a new £0 order.
          </p>
          {data.canRecord ? (
            <>
              <p>
                If you have already returned the full remaining{" "}
                {formatCurrency(data.remainingAmount, order.currency)} to the
                buyer, record it here.
              </p>
              <p className="text-sm text-gray-600">
                This records your confirmation; it does not send money or update
                accounting software.
              </p>
              <Button
                className="self-start"
                disabled={query.isFetching || query.isError}
                onClick={() => setDialogActive(true)}
              >
                Record completed external refund
              </Button>
            </>
          ) : (
            <p>{data.unavailableReason}</p>
          )}
        </>
      )}
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
        <dt>Recorded payments</dt>
        <dd>{formatCurrency(data.paidAmount, order.currency)}</dd>
        <dt>Financially recorded refunds</dt>
        <dd>{formatCurrency(data.recordedRefundedAmount, order.currency)}</dd>
      </dl>
      {dialogActive && data.canRecord && (
        <OrderExternalRefundDialog
          orderId={order.id}
          currency={order.currency}
          buyerName={order.Retailer.Business.name}
          remainingAmount={data.remainingAmount}
          refreshing={query.isFetching}
          refreshBalance={async () => {
            const result = await query.refetch({ throwOnError: true });
            if (!result.data) {
              throw new Error("Unable to refresh the refundable balance.");
            }
            return result.data;
          }}
          onDismiss={() => setDialogActive(false)}
        />
      )}
    </Card>
  );
}
